import { Injectable } from '@angular/core';
import { createEffect } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { filter, map, tap, withLatestFrom } from 'rxjs/operators';
import { AppStore } from 'src/app/store/store.model';
import { getTheme } from 'src/app/store/ui/ui.selectors';
import { PopupNotificationType } from 'studio-components';
import { PopupNotificationService } from '../popup-notification.service';
import { EffectInitiator } from './types/effect-initiator.type';
import { EffectCreatorUtilsService } from './utils/effect-creator-utils.service';

export type PopupNotificationEffectCreatorArgs<TProps = any> = {
  initiator: EffectInitiator<TProps>;
  notificationType: PopupNotificationType;
  shouldDisplay?: (props: TProps) => boolean;
  title: (props?: TProps) => string;
  message: (props?: TProps) => string;
  buttonLabel?: string;
  persistent?: boolean;
};

@Injectable({
  providedIn: 'root',
})
export class PopupNotificationEffectCreatorService {
  /**
   * Creates an effect which displays a popup notification with the specified parameters and the active theme
   * @param options options for the effect
   * @param options.initiator action or selector which triggers display of the notification
   * @param options.shouldDisplay optional filter function to determine whether to display the notification
   * @param options.notificationType type of notification
   * @param options.title title of the notification
   * @param options.message message to display in the notification
   * @param options.persistent true if the notification should not auto dismiss, defaults to false
   */
  createEffect<TProps>(options: PopupNotificationEffectCreatorArgs<TProps>) {
    const { initiator, notificationType, shouldDisplay, title, message, persistent } = options;

    return createEffect(
      () =>
        this.effectCreatorUtils.createInitiatingObservable<TProps>(initiator).pipe(
          filter((props) => !shouldDisplay || shouldDisplay(props)),
          map((props) => [title, message].map((func) => func(props))),
          withLatestFrom(this.store.select(getTheme)),
          tap(([[titleResult, messageResult], theme]) =>
            this.popupNotificationService.openNotification(
              theme,
              notificationType,
              titleResult,
              messageResult,
              persistent ? 0 : 3000,
            ),
          ),
        ),
      { dispatch: false },
    );
  }

  constructor(
    private effectCreatorUtils: EffectCreatorUtilsService,
    private popupNotificationService: PopupNotificationService,
    private store: Store<AppStore>,
  ) {}
}
